import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const defaultForm = {
  enabled: true,
  smtpHost: '',
  smtpPort: 587,
  useTls: true,
  useSsl: false,
  smtpUsername: '',
  smtpPassword: '',
  fromName: ''
};

const EmailSettings = () => {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get('/api/email/settings');
        if (response.data) {
          setForm({ ...defaultForm, ...response.data });
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load email settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setMessage('');
    setError('');
  };

  const handleTest = async () => {
    setTesting(true);
    setMessage('');
    setError('');

    try {
      await axios.post('/api/email/settings/test', form);
      setMessage('SMTP connection successful.');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'SMTP test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');

    try {
      await axios.put('/api/email/settings', form);
      setMessage('Email settings saved successfully.');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 px-4 py-6 sm:px-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Settings</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure the shared SMTP login used for candidate emails.
          </p>
        </div>

        <div className="rounded-3xl border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="px-6 py-10 text-sm text-gray-500">Loading email settings...</div>
          ) : (
            <div className="space-y-6 px-6 py-6">
              <div className="flex items-center justify-between rounded-2xl bg-gray-50 px-4 py-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Enable outgoing candidate emails</p>
                  <p className="text-xs text-gray-500">Automatic sends only happen when a matching active template exists.</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleChange('enabled', !form.enabled)}
                  className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${
                    form.enabled ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                      form.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <label className="space-y-2">
                  <span className="text-sm font-medium text-gray-700">SMTP Host</span>
                  <input
                    value={form.smtpHost}
                    onChange={(event) => handleChange('smtpHost', event.target.value)}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="smtp.zoho.com"
                  />
                </label>

                <label className="space-y-2">
                  <span className="text-sm font-medium text-gray-700">SMTP Port</span>
                  <input
                    type="number"
                    value={form.smtpPort}
                    onChange={(event) => handleChange('smtpPort', Number(event.target.value))}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </label>

                <label className="space-y-2">
                  <span className="text-sm font-medium text-gray-700">SMTP Username</span>
                  <input
                    value={form.smtpUsername}
                    onChange={(event) => handleChange('smtpUsername', event.target.value)}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="you@company.com"
                  />
                </label>

                <label className="space-y-2">
                  <span className="text-sm font-medium text-gray-700">From Name</span>
                  <input
                    value={form.fromName}
                    onChange={(event) => handleChange('fromName', event.target.value)}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="Hiring Team"
                  />
                </label>

                <label className="space-y-2 md:col-span-2">
                  <span className="text-sm font-medium text-gray-700">SMTP Password</span>
                  <input
                    type="password"
                    value={form.smtpPassword}
                    onChange={(event) => handleChange('smtpPassword', event.target.value)}
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </label>
              </div>

              <div className="flex flex-wrap gap-6 rounded-2xl border border-gray-200 px-4 py-4">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.useTls}
                    onChange={(event) => handleChange('useTls', event.target.checked)}
                  />
                  Use TLS
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.useSsl}
                    onChange={(event) => handleChange('useSsl', event.target.checked)}
                  />
                  Use SSL
                </label>
              </div>

              {(message || error) && (
                <div className={`rounded-2xl px-4 py-3 text-sm ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                  {error || message}
                </div>
              )}

              <div className="flex flex-wrap justify-end gap-3">
                <button
                  type="button"
                  onClick={handleTest}
                  disabled={testing}
                  className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {testing ? 'Testing...' : 'Test Connection'}
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default EmailSettings;
